import { useEffect, useMemo, useRef, useState } from 'react'

import {
  ComparisonSide,
  ComparisonWorkerClient,
  ComparisonWithSource,
  WebRendererMode,
} from '@sketch-hq/sketch-web-renderer'
import { useGetPagePresentationFileQuery } from '@sketch/gql-types'

import { useCanvasFilePath } from '../../../components/WebRendererCanvas/hooks/useCanvasFilePath'
import { useCanvasScope } from '../../canvas/scope'
import { presentationURLFormats } from '../../canvas/utils'
import { useVersioning } from '../ShareVersionContext'
import { scopeToEntity } from './groupChanges'
import { EntityChange, LayerChange } from './layerTypes'
import { isAbsentFromCanvas } from './utils'
import { ComparedVersions, useCompareVersions } from './useCompareVersions'

const IS_DEV_OR_TEST_ENV =
  process.env.REACT_APP_ENV === 'dev' || process.env.REACT_APP_ENV === 'test'

export interface VersionComparison {
  /** The pair being compared, ordered, or null when nothing is. */
  versions: ComparedVersions | null
  /**
   * The two versions as one id, for anything that has to notice when the pair
   * changes. By their short ids rather than by this object, which is rebuilt
   * whenever a fetch flag flips or a frame is opened, all while the same two
   * versions are being compared.
   */
  pairId: string | null
  /** The frames and Sections that changed, or null while nothing is compared. */
  entities: EntityChange[] | null
  /** Every layer that changed, in any entity. */
  layers: LayerChange[] | null
  /** The changed entities by uuid. */
  entityByUUID: Map<string, EntityChange>
  /** The changed entities the canvas has no node for, drawn from the other version. */
  absentEntities: EntityChange[]
  loading: boolean
  /**
   * A version's presentation file does not exist yet. It is generated on the
   * server and `downloadUrl` is null until it is; nothing here polls for it.
   * Kept apart from an empty result, which would otherwise read as "nothing
   * changed" about two versions that were never read.
   */
  isAwaitingAssets: boolean
  /**
   * The page is not in the version it is being compared with - it was added
   * after it, or deleted before. Only ever the other version: the one on
   * screen is where the page came from.
   */
  isPageMissing: boolean
  error: Error | null
  /**
   * Either version was read before all of it had arrived. What never arrived
   * is absent from both sides, so an edit inside it compares equal.
   */
  isPartial: boolean
  /** The worker's reply, with the version the canvas is not showing. */
  comparison: ComparisonWithSource | null
}

const BASE_STATE: OmitSafe<VersionComparison, 'versions' | 'pairId'> = {
  entities: null,
  layers: null,
  entityByUUID: new Map(),
  absentEntities: [],
  loading: false,
  isAwaitingAssets: false,
  isPageMissing: false,
  error: null,
  isPartial: false,
  comparison: null,
}

type Reading =
  | { state: 'idle' }
  | { state: 'loading' }
  | { state: 'done'; comparison: ComparisonWithSource }
  | { state: 'failed'; error: Error }

/**
 * What changed on this page between the two compared versions.
 *
 * Both versions are read from their presentation files in a worker of their
 * own, not from the canvas: the canvas only fetches fragments for what it
 * draws, so a tree read from it would call every unvisited layer deleted, and
 * it holds one file that it would have to give up to read the other.
 */
export function useVersionComparison(pageUUID?: string): VersionComparison {
  const { share } = useVersioning()
  const { versions } = useCompareVersions()
  // What the canvas is showing: a frame or Section view is about that entity
  // alone, so the changes elsewhere on the page are not part of it.
  const focusedEntityUUID = useCanvasScope()?.entityUUID

  const [reading, setReading] = useState<Reading>({ state: 'idle' })

  const skip = !versions || !pageUUID

  const older = useGetPagePresentationFileQuery({
    variables: {
      shareIdentifier: share.identifier,
      versionShortId: versions?.older.shortId,
      pageUUID,
    },
    skip,
  })

  const newer = useGetPagePresentationFileQuery({
    variables: {
      shareIdentifier: share.identifier,
      versionShortId: versions?.newer.shortId,
      pageUUID,
    },
    skip,
  })

  const olderPage = older.data?.share?.version?.document?.page
  const newerPage = newer.data?.share?.version?.document?.page
  const olderFile = olderPage?.presentationFile
  const newerFile = newerPage?.presentationFile

  // Pinned the way the canvas pins its own file: a refetch re-signs the same
  // file under a new URL, and that must not start the comparison over.
  const olderURL = useCanvasFilePath({
    downloadUrl: olderFile?.downloadUrl ?? undefined,
  })
  const newerURL = useCanvasFilePath({
    downloadUrl: newerFile?.downloadUrl ?? undefined,
  })

  // Re-issued on refetch as well, and any current one is good - so read when
  // the comparison starts rather than restarting it.
  const tokens = useRef({
    older: olderFile?.imageToken,
    newer: newerFile?.imageToken,
  })
  tokens.current = {
    older: olderFile?.imageToken,
    newer: newerFile?.imageToken,
  }

  const isFetchingSources = !skip && (older.loading || newer.loading)

  // Each of these leaves a version without a file to read, and each needs
  // saying differently: only a file still being generated is worth a reload.
  const queryError = skip ? undefined : (older.error ?? newer.error)
  const hasSettled = !skip && !isFetchingSources && !queryError
  const isPageMissing = hasSettled && (!olderPage || !newerPage)
  const isAwaitingAssets =
    hasSettled && !isPageMissing && (!olderURL || !newerURL)

  // The side the canvas is not showing, kept so it can draw what it lacks
  const otherSide: ComparisonSide | null = versions
    ? versions.isViewingNewer
      ? 'before'
      : 'after'
    : null

  useEffect(() => {
    if (!pageUUID || !olderURL || !newerURL || !otherSide) {
      setReading({ state: 'idle' })
      return
    }

    const worker = new ComparisonWorkerClient({
      locateFile: `${process.env.WEB_RENDERER_ASSETS_FOLDER}/{file}`,
      mode: WebRendererMode.release,
    })

    let isCurrent = true
    let isDisposed = false

    // Its WASM heap sits beside the canvas's: let it go as soon as possible
    const dispose = () => {
      if (isDisposed) return
      isDisposed = true
      worker.dispose()
    }

    setReading({ state: 'loading' })

    const olderURLs = presentationURLFormats(pageUUID, tokens.current.older)
    const newerURLs = presentationURLFormats(pageUUID, tokens.current.newer)

    worker
      .compare(
        // An image is only served with the token of a version that has it
        { fileURL: olderURL, ...olderURLs },
        { fileURL: newerURL, ...newerURLs },
        { keep: otherSide }
      )
      .then(comparison => {
        if (isCurrent) setReading({ state: 'done', comparison })
      })
      .catch(error => {
        if (!isCurrent) return

        if (IS_DEV_OR_TEST_ENV) {
          // eslint-disable-next-line no-console
          console.error('[compare] could not read both versions', error)
        }

        setReading({
          state: 'failed',
          error: error instanceof Error ? error : new Error(String(error)),
        })
      })
      .finally(dispose)

    return () => {
      isCurrent = false
      dispose()
    }
  }, [pageUUID, olderURL, newerURL, otherSide])

  const pairId = versions
    ? `${versions.older.shortId}:${versions.newer.shortId}`
    : null

  return useMemo(() => {
    switch (reading.state) {
      case 'failed':
        return { ...BASE_STATE, versions, pairId, error: reading.error }

      case 'done': {
        const isViewingNewer = versions?.isViewingNewer ?? true
        const { entities, layers } = scopeToEntity(
          reading.comparison.differences.entities,
          reading.comparison.differences.layers,
          focusedEntityUUID
        )

        return {
          ...BASE_STATE,
          versions,
          pairId,
          entities,
          entityByUUID: new Map(entities.map(entity => [entity.uuid, entity])),
          absentEntities: entities.filter(entity =>
            isAbsentFromCanvas(entity.kind, isViewingNewer)
          ),
          layers,
          isPartial: reading.comparison.differences.isPartial,
          comparison: reading.comparison,
        }
      }

      default:
        return {
          ...BASE_STATE,
          versions,
          pairId,
          loading: reading.state === 'loading' || isFetchingSources,
          isAwaitingAssets,
          isPageMissing,
          error: queryError ?? null,
        }
    }
  }, [
    reading,
    focusedEntityUUID,
    versions,
    pairId,
    isFetchingSources,
    isAwaitingAssets,
    isPageMissing,
    queryError,
  ])
}
